import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

import { SettingsModal } from "@/components/layout/SettingsModal";
import { ProfileModal } from "@/components/layout/ProfileModal";

interface SettingsModalContextValue {
  openSettingsModal: () => void;
  closeSettingsModal: () => void;
  openProfileModal: () => void;
}

const SettingsModalContext = createContext<SettingsModalContextValue | null>(null);
const settingsModalFallback: SettingsModalContextValue = {
  openSettingsModal: () => undefined,
  closeSettingsModal: () => undefined,
  openProfileModal: () => undefined,
};

export function SettingsModalProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  const openSettingsModal = useCallback(() => {
    setOpen(true);
  }, []);

  const closeSettingsModal = useCallback(() => {
    setOpen(false);
  }, []);
  const openProfileModal = useCallback(() => setProfileOpen(true), []);

  const value = useMemo(
    () => ({ openSettingsModal, closeSettingsModal, openProfileModal }),
    [openSettingsModal, closeSettingsModal, openProfileModal],
  );

  return (
    <SettingsModalContext.Provider value={value}>
      {children}
      {open && <SettingsModal onClose={closeSettingsModal} />}
      {profileOpen && <ProfileModal onClose={() => setProfileOpen(false)} />}
    </SettingsModalContext.Provider>
  );
}

export function useSettingsModal() {
  return useContext(SettingsModalContext) ?? settingsModalFallback;
}
